/**
 * P_WEEKLY_REPORT_CARD_2.3 — Archive tab.
 *
 * Calm, scannable list of past published snapshots. Each row is a
 * deep-link into the Current Report tab with `?snapshot_id=...`,
 * which forces the portal to render that immutable week.
 *
 * Per feature freeze: no analytics, no charts, no filters yet. Just
 * the publication's table of contents.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { weeklyReportAPI } from '../../lib/api';

export default function WeeklyReportArchive({ organizationId, t }) {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!organizationId) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                // History is super_admin only on the backend. Client
                // admins reach this tab via the same component but the
                // request 403s — we degrade silently to "no items".
                const res = await weeklyReportAPI.history(organizationId, 52);
                if (!cancelled) setItems(res.data?.items || []);
            } catch {
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [organizationId]);

    if (loading) {
        return (
            <div className="py-32 flex flex-col items-center gap-6" data-testid="weekly-report-archive-loading">
                <Loader2 className="w-8 h-8 animate-spin opacity-50" />
            </div>
        );
    }

    return (
        <section data-testid="weekly-report-archive">
            <h2 className="text-xs sm:text-sm uppercase tracking-[0.32em] opacity-60 mb-16 sm:mb-20">
                {t('weeklyReport.archive.title')}
            </h2>

            {items.length === 0 ? (
                <p className="text-base sm:text-lg opacity-50" data-testid="weekly-report-archive-empty">
                    {t('weeklyReport.archive.empty')}
                </p>
            ) : (
                <ul className="space-y-10 sm:space-y-12">
                    {items.map((it) => (
                        <li key={it.id}>
                            <button
                                type="button"
                                onClick={() => navigate(`/weekly-report?snapshot_id=${it.id}`)}
                                data-testid={`weekly-report-archive-row-${it.id}`}
                                className="w-full text-left group"
                            >
                                <p className="text-2xl sm:text-3xl font-light tracking-tight leading-snug group-hover:opacity-80 transition-opacity">
                                    {t('weeklyReport.weekEnding', { date: it.week_end })}
                                </p>
                                <p className="mt-1 text-xs uppercase tracking-[0.2em] opacity-40">
                                    {new Date(it.created_at).toLocaleDateString(undefined, {
                                        year: 'numeric', month: 'short', day: 'numeric',
                                    })}
                                </p>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
